/**
 * XLSX Chat Sidebar Component
 * 
 * Chat interface for interacting with AI about the loaded XLSX file.
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { Send } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChartRenderer } from './ChartRenderer';

interface XLSXChatSidebarProps {
  sessionId: string;
  conversationId: string;
  fileName: string;
  selectedCell: any;
  selectedRange?: any;
  activeSheet?: { name: string; index: number } | null;
  onDataModified?: (modifications: any[]) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const XLSXChatSidebar: React.FC<XLSXChatSidebarProps> = ({
  sessionId,
  conversationId,
  fileName,
  selectedCell,
  selectedRange,
  activeSheet,
  onDataModified
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedMessage, setExpandedMessage] = useState<ChatMessage | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Initial AI message
    setMessages([{
      role: 'assistant',
      content: `Hi! I've loaded **${fileName}**. I can help you: • Analyze data and find insights • Create and explain formulas • Format cells and columns • Answer questions about the spreadsheet • Find specific data What would you like to do?`,
      timestamp: new Date()
    }]);
  }, [fileName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsProcessing(true);

    // Prepare AI message placeholder
    const aiMessageIndex = messages.length + 1;

    try {
      // Build conversation history for context (content must be string, not array)
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
      }));

      // Call streaming chat API
      const response = await fetch(`${API_BASE_URL}/xlsx-chat/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId,
          message: userInput,
          conversationHistory,
          selectedRange: selectedRange,
          selectedCell: selectedCell,
          activeSheet: activeSheet
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle Server-Sent Events stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body reader available');
      }

      let currentAiContent = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'text') {
                // Append text chunk to AI message
                currentAiContent += data.content;
                
                // Update or create AI message
                setMessages(prev => {
                  const newMessages = [...prev];
                  const existingAiMsgIndex = newMessages.findIndex((m, i) => i === aiMessageIndex && m.role === 'assistant');
                  
                  if (existingAiMsgIndex >= 0) {
                    newMessages[existingAiMsgIndex] = {
                      ...newMessages[existingAiMsgIndex],
                      content: currentAiContent
                    };
                  } else {
                    newMessages.push({
                      role: 'assistant',
                      content: currentAiContent,
                      timestamp: new Date()
                    });
                  }
                  
                  return newMessages;
                });
              } else if (data.type === 'done') {
                console.log('✅ Chat complete. Modifications:', data.modifications);
                // Notify parent to apply modifications if there were any
                if (data.modifications && data.modifications.length > 0 && onDataModified) {
                  onDataModified(data.modifications);
                }
              } else if (data.type === 'error') {
                console.error('❌ Stream error:', data.error);
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        role: 'system',
        content: `Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', boxShadow: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2.5, borderBottom: 2, borderColor: 'divider', bgcolor: 'primary.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Box 
            component="img" 
            src="/LuminaAilogoPNG.png" 
            alt="Lumina AI" 
            sx={{ 
              height: 48,
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">File:</Typography>
            <Typography variant="caption" fontWeight="medium" noWrap sx={{ flex: 1 }}>{fileName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Session:</Typography>
            <Chip label={sessionId.slice(0, 8) + '...'} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
          </Box>
        </Box>
      </Paper>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 2
            }}
          >
            <Paper
              elevation={2}
              onDoubleClick={() => setExpandedMessage(message)}
              sx={{
                maxWidth: '85%',
                px: 2,
                py: 1.5,
                bgcolor: message.role === 'user' 
                  ? 'primary.main' 
                  : message.role === 'system'
                  ? 'error.light'
                  : 'background.paper',
                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                cursor: 'pointer',
                transition: 'transform 0.1s',
                '&:hover': {
                  transform: 'scale(1.01)',
                  boxShadow: 4
                },
                '& .markdown-content': {
                  '& p': { margin: '0.5em 0' },
                  '& p:first-of-type': { marginTop: 0 },
                  '& p:last-of-type': { marginBottom: 0 },
                  '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                  '& li': { marginBottom: '0.25em' },
                  '& code': { 
                    bgcolor: message.role === 'user' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  },
                  '& pre': { 
                    bgcolor: message.role === 'user' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                    p: 1,
                    borderRadius: 1,
                    overflow: 'auto',
                    '& code': {
                      bgcolor: 'transparent',
                      p: 0
                    }
                  },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& h1, & h2, & h3, & h4, & h5, & h6': { 
                    margin: '0.75em 0 0.5em 0',
                    fontWeight: 600
                  },
                  '& blockquote': {
                    borderLeft: '3px solid',
                    borderColor: message.role === 'user' ? 'rgba(255,255,255,0.5)' : 'primary.main',
                    pl: 1.5,
                    ml: 0,
                    fontStyle: 'italic'
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    fontSize: '0.9em'
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: message.role === 'user' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.12)',
                    padding: '0.5em',
                    textAlign: 'left'
                  },
                  '& th': {
                    fontWeight: 600,
                    bgcolor: message.role === 'user' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                  }
                }
              }}
            >
              <Box className="markdown-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <Typography variant="body2" component="p" sx={{ display: 'inline-block', width: '100%' }}>
                        {children}
                      </Typography>
                    ),
                    li: ({ children }) => (
                      <Typography variant="body2" component="li">
                        {children}
                      </Typography>
                    ),
                    code: ({ node, inline, className, children, ...props }: any) => {
                      const match = /language-chart:(\w+)/.exec(className || '');
                      const chartType = match ? match[1] : null;
                      
                      if (!inline && chartType) {
                        const childString = String(children).trim();
                        
                        // Don't try to parse if JSON looks incomplete (during streaming)
                        if (!childString || childString.length < 10) {
                          return (
                            <Box sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1, my: 1 }}>
                              <Typography variant="caption">
                                Loading chart...
                              </Typography>
                            </Box>
                          );
                        }
                        
                        // Check if JSON looks complete (has closing brace)
                        if (!childString.endsWith('}') && !childString.endsWith(']')) {
                          return (
                            <Box sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1, my: 1 }}>
                              <Typography variant="caption">
                                Loading chart data...
                              </Typography>
                            </Box>
                          );
                        }
                        
                        try {
                          const chartData = JSON.parse(childString.replace(/\n$/, ''));
                          return <ChartRenderer type={chartType} data={chartData} />;
                        } catch (err) {
                          // During streaming, incomplete JSON is normal - show loading
                          if (isProcessing) {
                            return (
                              <Box sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1, my: 1 }}>
                                <Typography variant="caption">
                                  Loading chart data...
                                </Typography>
                              </Box>
                            );
                          }
                          
                          // After streaming is done, this is a real error
                          console.error('Error parsing chart data:', err);
                          return (
                            <Box sx={{ p: 1, bgcolor: 'error.light', borderRadius: 1, my: 1 }}>
                              <Typography variant="caption" color="error">
                                Invalid chart data format
                              </Typography>
                            </Box>
                          );
                        }
                      }
                      
                      // Regular code rendering
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 0.5, 
                  display: 'block',
                  color: message.role === 'user' ? 'primary.50' : 'text.secondary'
                }}
              >
                {message.timestamp.toLocaleTimeString()}
              </Typography>
            </Paper>
          </Box>
        ))}
        
        {isProcessing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Paper elevation={2} sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" fontWeight="medium">AI is thinking...</Typography>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Paper elevation={0} sx={{ p: 2, borderTop: 2, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your spreadsheet..."
            multiline
            rows={2}
            disabled={isProcessing}
            fullWidth
            variant="outlined"
            size="small"
          />
          <IconButton
            onClick={handleSend}
            disabled={isProcessing || !input.trim()}
            color="primary"
            sx={{ alignSelf: 'flex-end' }}
          >
            <Send />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Press <strong>Enter</strong> to send, <strong>Shift+Enter</strong> for new line
        </Typography>
      </Paper>

      {/* Expanded Message Modal */}
      <Dialog
        open={!!expandedMessage}
        onClose={() => setExpandedMessage(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        {expandedMessage && (
          <>
            <DialogTitle sx={{ 
              bgcolor: expandedMessage.role === 'user' ? 'primary.main' : 'background.paper',
              color: expandedMessage.role === 'user' ? 'primary.contrastText' : 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{ flex: 1 }}>
                {expandedMessage.role === 'user' ? '💬 Your Message' : 
                 expandedMessage.role === 'system' ? '⚠️ System Message' : 
                 '🤖 AI Response'}
              </Box>
              <Typography variant="caption" component="span" sx={{ opacity: 0.8 }}>
                {expandedMessage.timestamp?.toLocaleTimeString()}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ 
              mt: 2,
              '& p': { margin: '0.5em 0', fontSize: '1rem', lineHeight: 1.7 },
              '& p:first-of-type': { marginTop: 0 },
              '& p:last-of-type': { marginBottom: 0 },
              '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
              '& li': { marginBottom: '0.25em' },
              '& code': { 
                bgcolor: 'rgba(0,0,0,0.05)',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.9em',
                fontFamily: 'monospace'
              },
              '& pre': { 
                bgcolor: 'rgba(0,0,0,0.05)',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                '& code': {
                  bgcolor: 'transparent',
                  p: 0
                }
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                my: 1
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left'
              },
              '& th': {
                bgcolor: 'grey.100',
                fontWeight: 'bold'
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                my: 1,
                fontStyle: 'italic',
                color: 'text.secondary'
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: 'bold'
              },
              '& h1': { fontSize: '2rem' },
              '& h2': { fontSize: '1.5rem' },
              '& h3': { fontSize: '1.25rem' }
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({node, ...props}) => <Typography paragraph {...props} />,
                  code: ({node, inline, className, children, ...props}: any) => {
                    // Check if this is a chart code block
                    const match = /language-chart:(\w+)/.exec(className || '');
                    const chartType = match ? match[1] : null;
                    
                    if (!inline && chartType) {
                      const childString = String(children).trim();
                      
                      // Check if JSON looks complete
                      if (!childString || childString.length < 10 || 
                          (!childString.endsWith('}') && !childString.endsWith(']'))) {
                        return (
                          <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, my: 1 }}>
                            <Typography variant="body2">
                              Loading chart data...
                            </Typography>
                          </Box>
                        );
                      }
                      
                      try {
                        const chartData = JSON.parse(childString);
                        return <ChartRenderer type={chartType} data={chartData} />;
                      } catch (err) {
                        console.error('Error parsing chart data:', err);
                        return (
                          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, my: 1 }}>
                            <Typography variant="body2" color="error">
                              Invalid chart data format
                            </Typography>
                          </Box>
                        );
                      }
                    }
                    
                    // Regular code rendering
                    return inline ? <code {...props}>{children}</code> : <pre><code {...props}>{children}</code></pre>;
                  },
                  table: ({node, ...props}) => <table {...props} />,
                  th: ({node, ...props}) => <th {...props} />,
                  td: ({node, ...props}) => <td {...props} />,
                  ul: ({node, ...props}) => <ul {...props} />,
                  ol: ({node, ...props}) => <ol {...props} />,
                  li: ({node, ...props}) => <li {...props} />,
                  blockquote: ({node, ...props}) => <blockquote {...props} />,
                  h1: ({node, ...props}) => <Typography variant="h1" {...props} />,
                  h2: ({node, ...props}) => <Typography variant="h2" {...props} />,
                  h3: ({node, ...props}) => <Typography variant="h3" {...props} />,
                  h4: ({node, ...props}) => <Typography variant="h4" {...props} />,
                  h5: ({node, ...props}) => <Typography variant="h5" {...props} />,
                  h6: ({node, ...props}) => <Typography variant="h6" {...props} />,
                }}
              >
                {typeof expandedMessage.content === 'string' 
                  ? expandedMessage.content 
                  : JSON.stringify(expandedMessage.content)}
              </ReactMarkdown>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpandedMessage(null)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
